import { useCallback, useMemo, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useAppSelector } from '@user/store/hooks/useAppSelector'
import {
  selectMapEntityDetails,
  selectMapEntityVariant,
  selectServiceLocationId,
  setEntityDetails,
} from '@user/store/map/slice'
import { deleteKnockStatusLocation } from '@user/store/knockStatus/api'
import { toast } from '@sales-center/components'
import { EntityDetail, EntityLocationDTO } from '@user/store/map/typings'

const mapVariantToEntityType = (variant: string | null): string => {
  switch (variant?.toLowerCase()) {
    case 'opportunity':
      return 'Opportunity'
    case 'lead':
      return 'Lead'
    case 'pendinglead':
    case 'draftlead':
      return 'DraftLead'
    case 'knockstatus':
      return 'KnockStatus'
    default:
      return 'Unknown'
  }
}

type Props = {
  address: EntityLocationDTO | null
  activeKnockStatusLocationId: number | null
  onRefresh?: () => void
  onDeleteSuccess: () => void
}

export function useDeleteHistory({
  address,
  activeKnockStatusLocationId,
  onRefresh,
  onDeleteSuccess,
}: Props) {
  const dispatch = useAppDispatch()
  const entityDetails = useAppSelector(selectMapEntityDetails)
  const variant = useAppSelector(selectMapEntityVariant)
  const selectedServiceLocationId = useAppSelector(selectServiceLocationId)
  const [deleteHistoryConfirmOpen, setDeleteHistoryConfirmOpen] =
    useState(false)
  const [isDeletingHistory, setIsDeletingHistory] = useState(false)

  const isDraftLead =
    variant?.toLowerCase() === 'draftlead' ||
    variant?.toLowerCase() === 'pendinglead'

  const entityType = useMemo(() => {
    const mapped = mapVariantToEntityType(
      variant ?? address?.entityType ?? null,
    )
    return mapped === 'Unknown' ? 'KnockStatus' : mapped
  }, [variant, address])

  const knockStatusLocationId = useMemo(() => {
    if (address && !address.entityId) {
      return activeKnockStatusLocationId
    }
    // Entity details not loaded (variant unknown) — mirror the useHistoryData
    // pattern and fall back to the location ID already resolved from history.
    if (!entityDetails) return activeKnockStatusLocationId ?? null
    if (isDraftLead) {
      return entityDetails?.knockStatusDetails?.knockStatusLocationId ?? null
    }
    return (
      entityDetails?.locations.serviceLocations.find(
        (item) => item.id === selectedServiceLocationId,
      )?.knockStatusDetails?.knockStatusLocationId ?? null
    )
  }, [
    entityDetails,
    isDraftLead,
    selectedServiceLocationId,
    address,
    activeKnockStatusLocationId,
  ])

  const openDeleteHistoryConfirm = useCallback(() => {
    setDeleteHistoryConfirmOpen(true)
  }, [])

  const closeDeleteHistoryConfirm = useCallback(() => {
    setDeleteHistoryConfirmOpen(false)
  }, [])

  const handleDeleteHistory = useCallback(async () => {
    if (!knockStatusLocationId) return
    setIsDeletingHistory(true)
    try {
      // entityType is already resolved at the hook level: 'Unknown' → 'KnockStatus'.
      const resolvedEntityType = entityType

      await dispatch(
        deleteKnockStatusLocation({
          locationId: knockStatusLocationId,
          entityType: resolvedEntityType,
        }),
      ).unwrap()

      if (entityDetails) {
        if (!isDraftLead) {
          dispatch(
            setEntityDetails({
              ...entityDetails,
              locations: {
                ...entityDetails.locations,
                serviceLocations: entityDetails.locations.serviceLocations.map(
                  (loc) => {
                    if (loc.id === selectedServiceLocationId) {
                      const { knockStatusDetails: _, ...rest } = loc
                      return rest
                    }
                    return loc
                  },
                ),
              },
            }),
          )
        } else {
          const { knockStatusDetails: _, ...restEntity } = entityDetails
          dispatch(setEntityDetails(restEntity as EntityDetail))
        }
      }

      toast.success('Knock status deleted')
      setDeleteHistoryConfirmOpen(false)
      onRefresh?.()
      onDeleteSuccess()
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete knock status')
    } finally {
      setIsDeletingHistory(false)
    }
  }, [
    knockStatusLocationId,
    entityType,
    address,
    entityDetails,
    isDraftLead,
    selectedServiceLocationId,
    dispatch,
    onRefresh,
    onDeleteSuccess,
  ])

  return {
    deleteHistoryConfirmOpen,
    isDeletingHistory,
    openDeleteHistoryConfirm,
    closeDeleteHistoryConfirm,
    handleDeleteHistory,
  }
}
